import React, { useId, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useWebLocale } from '../../../../contexts/WebLocaleContext';

const VALUES = ['reviews', 'tracks'];
const INTERACTIVE = 'button, a, input, textarea, select, [role="slider"], [contenteditable="true"]';

// Keep both panels mounted: switching tabs must not discard a draft or stop a preview.
// The parent keys this component by album identity, never by viewport or query result object.
export default function AlbumContentPager({
  reviews,
  tracks,
  initialTab = 'reviews',
  onTabChange,
}) {
  const { locale } = useWebLocale();
  const [active, setActive] = useState(initialTab === 'tracks' ? 'tracks' : 'reviews');
  const select = (tab) => {
    setActive(tab);
    onTabChange?.(tab);
  };
  const wide = useMediaQuery('(min-width:1280px)');
  const id = useId();
  const buttons = useRef([]);
  const gesture = useRef(null);
  const labels = locale === 'en' ? ['Reviews & Ratings', 'Tracklist'] : ['리뷰·평가', '트랙리스트'];

  const onKeyDown = (event, index) => {
    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % VALUES.length;
    if (event.key === 'ArrowLeft') next = (index + VALUES.length - 1) % VALUES.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = VALUES.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    select(VALUES[next]);
    buttons.current[next]?.focus();
  };

  const onTouchStart = (event) => {
    gesture.current = null;
    if (wide || event.touches.length !== 1 || event.target.closest(INTERACTIVE)) return;
    const touch = event.touches[0];
    // Leave browser back/forward edge gestures alone.
    if (touch.clientX < 16 || touch.clientX > window.innerWidth - 16) return;
    gesture.current = { x: touch.clientX, y: touch.clientY };
  };
  const onTouchEnd = (event) => {
    const start = gesture.current;
    gesture.current = null;
    const end = event.changedTouches[0];
    if (!start || !end || event.touches.length) return;
    const dx = end.clientX - start.x;
    const dy = end.clientY - start.y;
    const threshold = Math.max(40, event.currentTarget.clientWidth * 0.12);
    if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy) * 2) return;
    select(dx < 0 ? 'tracks' : 'reviews');
  };

  return (
    <section className={`pp-album-pager${wide ? ' is-wide' : ''}`}>
      <div
        className="pp-album-content-tabs"
        hidden={wide}
        role="tablist"
        aria-label={locale === 'en' ? 'Album content' : '앨범 콘텐츠'}
      >
        {VALUES.map((value, index) => (
          <button
            key={value}
            ref={(element) => (buttons.current[index] = element)}
            type="button"
            role="tab"
            id={`${id}-${value}-tab`}
            aria-controls={`${id}-${value}-panel`}
            aria-selected={active === value}
            tabIndex={active === value ? 0 : -1}
            onClick={() => select(value)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            <span>{labels[index]}</span>
          </button>
        ))}
      </div>
      {VALUES.map((value, index) => (
        <div
          key={value}
          className="pp-album-content-panel"
          role={wide ? 'region' : 'tabpanel'}
          id={`${id}-${value}-panel`}
          aria-labelledby={`${id}-${value}-${wide ? 'heading' : 'tab'}`}
          tabIndex={0}
          hidden={!wide && active !== value}
          onTouchStart={onTouchStart}
          onTouchMove={(event) => {
            const start = gesture.current;
            const point = event.touches[0];
            if (
              event.touches.length !== 1 ||
              (start &&
                point &&
                Math.abs(point.clientY - start.y) > 24 &&
                Math.abs(point.clientY - start.y) > Math.abs(point.clientX - start.x))
            )
              gesture.current = null;
          }}
          onTouchEnd={onTouchEnd}
          onTouchCancel={() => (gesture.current = null)}
        >
          <h2 className="pp-album-panel-heading" id={`${id}-${value}-heading`} hidden={!wide}>
            {labels[index]}
          </h2>
          {index === 0 ? reviews : tracks}
        </div>
      ))}
    </section>
  );
}
